'use client'

import {
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
  FieldLegend,
  FieldPanel,
  FieldSet,
  FieldTitle,
} from '@ui/base/field'
import { Input } from '@ui/base/input'
import { InputWithUnit } from '@ui/base/input-with-unit'
import { RadioGroup, RadioGroupItem } from '@ui/base/radio-group'
import { Textarea } from '@ui/base/textarea'
import { useFormContext } from 'react-hook-form'

import { type FulfillmentFormValues } from '../model/types'
import { FormBlock } from './form-block'
import { RequiredMark } from './required-mark'

const categoryOptions = [
  ['all', 'Все категории'],
  ['excluding', 'Есть исключения'],
] as const

type ScaleField = {
  name: 'areaSquareMeters' | 'operationStartedYear' | 'activeClientCount' | 'minimumUnitPriceRubles'
  id: string
  label: string
  inputMode: 'numeric' | 'decimal'
  /** Единица приставкой у поля. Есть только у денежного поля — у остальных она в подписи. */
  unit?: string
}

const scaleFields: ScaleField[] = [
  { name: 'areaSquareMeters', id: 'fulfillment-area', label: 'Площадь, м²', inputMode: 'numeric' },
  { name: 'operationStartedYear', id: 'fulfillment-year', label: 'Работаете с года', inputMode: 'numeric' },
  { name: 'activeClientCount', id: 'fulfillment-clients', label: 'Активных клиентов', inputMode: 'numeric' },
  // Валюта стоит приставкой у поля, поэтому в подписи её нет.
  { name: 'minimumUnitPriceRubles', id: 'fulfillment-price', label: 'Цена от', inputMode: 'decimal', unit: '₽' },
]

/** Третий шаг: какой товар берёте и какого размера ваш склад. */
export const GoodsStep = () => {
  const {
    register,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<FulfillmentFormValues>()
  const categoryMode = watch('categoryMode')

  return (
    <>
      <FormBlock title="Категории товаров" description="Если есть ограничения, перечислите их своими словами.">
        <FieldSet>
          <FieldLegend>Какие категории принимаете</FieldLegend>
          <RadioGroup
            value={categoryMode}
            onValueChange={(mode) =>
              setValue('categoryMode', mode as FulfillmentFormValues['categoryMode'], { shouldDirty: true })
            }
            className="sm:grid-cols-2"
          >
            {categoryOptions.map(([value, label]) => {
              const categoryId = `fulfillment-category-mode-${value}`
              return (
                <FieldLabel key={value} htmlFor={categoryId}>
                  <Field orientation="horizontal">
                    <RadioGroupItem id={categoryId} size="lg" value={value} aria-labelledby={`${categoryId}-title`} />
                    <FieldTitle id={`${categoryId}-title`}>{label}</FieldTitle>
                  </Field>
                </FieldLabel>
              )
            })}
          </RadioGroup>
        </FieldSet>
        {/* Поле исключений лежит на подложке: так видно, что оно относится к выбранному варианту. */}
        {categoryMode === 'excluding' && (
          <FieldPanel>
            <Field data-invalid={Boolean(errors.categoryExclusions?.message)}>
              <FieldLabel htmlFor="fulfillment-category-exclusions">
                Исключения категорий <RequiredMark />
              </FieldLabel>
              <Textarea
                id="fulfillment-category-exclusions"
                rows={4}
                aria-invalid={Boolean(errors.categoryExclusions?.message)}
                aria-describedby={
                  errors.categoryExclusions?.message
                    ? 'fulfillment-category-exclusions-error'
                    : 'fulfillment-category-exclusions-hint'
                }
                {...register('categoryExclusions')}
              />
              <FieldError id="fulfillment-category-exclusions-error">{errors.categoryExclusions?.message}</FieldError>
              <FieldDescription id="fulfillment-category-exclusions-hint">
                Например: КГТ, пожароопасные товары, продукты питания, БАДы, бытовая химия.
              </FieldDescription>
            </Field>
          </FieldPanel>
        )}
      </FormBlock>

      <FormBlock title="Масштаб и стоимость" description="Поля можно оставить пустыми, если данных пока нет.">
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {scaleFields.map(({ name, id, label, inputMode, unit }) => {
            const message = errors[name]?.message
            const control = {
              id,
              inputMode,
              'aria-invalid': Boolean(message),
              'aria-describedby': message ? `${id}-error` : undefined,
              ...register(name),
            }
            return (
              <Field key={name} data-invalid={Boolean(message)}>
                <FieldLabel htmlFor={id}>{label}</FieldLabel>
                {unit ? <InputWithUnit {...control} unit={unit} /> : <Input {...control} />}
                <FieldError id={`${id}-error`}>{message}</FieldError>
              </Field>
            )
          })}
        </div>
      </FormBlock>
    </>
  )
}
